import { Link as RouterLink }         from 'react-router-dom'
import BaseTable, { BaseSearch }      from '../ui/components/base-table'
import {
  Box,
  Button,
  Container, FormControl,
  Grid,
  InputLabel,
  Link, MenuItem, Select,
  Stack,
  Typography
}                                     from '@mui/material'
import Page                           from '../ui/components/Page'
import Iconify                        from '../ui/components/Iconify'
import { useTranslation }             from 'react-i18next'
import React, {
  useContext, useEffect, useState
}                                     from 'react'
import { useApi }                     from '../utils/api'
import { BaseBreadcrumbs }            from '../ui/components/BaseBreadcrumbs'
import Label                          from '../ui/components/Label'
import useCategories                  from '../hooks/useCategories'
import { AppContext }                 from '../App'

export default function ServicesPage() {

  const api = useApi()
  const { user } = useContext(AppContext)
  const { t } = useTranslation()
  const [categories] = useCategories()
  const [query, setQuery] = useState('')
  const [partners, setPartners] = useState([])
  const [selectedPartner, setSelectedPartner] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [reloadCounter, setReloadCounter] = useState(0)

  useEffect(() => {
    api.partner.list().then(({data}) => {
      setPartners(data)
    }).catch((e) => {
      console.error('Failed to load partners' ,e)
    })
  }, [])

  useEffect(() => {
    setReloadCounter((prev) => prev + 1)
  }, [selectedPartner, selectedCategory])

  const columns = [
    { field: 'id', headerName: t('ID'), width: 100, sortable: false, filterable: false },
    { field: 'title', headerName: t('Title'), flex: 2, sortable: false, filterable: false, renderCell: (cell) => {
        return (
          <Link
            component={RouterLink}
            variant="body2"
            to={`/services/${cell.row.id}`}
          >
            {cell.row.title}
          </Link>
        )
      }},
    { field: 'phone', headerName: t('Phone'), width: 70, sortable: false, filterable: false },
    { field: 'pattern', headerName: t('Pattern'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return <Label color={'default'}>
          <code>{cell.row.pattern}</code>
        </Label>
    }},
    { field: 'type', headerName: t('Type'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return t(`service_type-${cell.row.type}`)
      }},
    { field: 'partner', headerName: t('Partner'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return (
          <Link
            component={RouterLink}
            variant="body2"
            to={`/partners/${cell.row.partner.id}`}
          >
            {cell.row.partner.name}
          </Link>
        )
      }},
    { field: 'category', headerName: t('Category'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return cell.row.category ? (
          <Link
            component={RouterLink}
            variant="body2"
            to={`/categories/${cell.row.category.id}`}
          >
            {cell.row.category.name}
          </Link>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('Not set')}
          </Typography>
        )
      }},
  ];

  return (<Page title={t('Services')}>
    <Container>
      <BaseBreadcrumbs key={'breadcrumb-services'}
                       title={t('Services')}
                       items={[]} />
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h4" gutterBottom>{t('Services')}</Typography>
        <BaseSearch query={query} onQueryChanged={(query) => setQuery(query)} />
        <Box flexGrow={'1'} />
        <Button variant="contained"
                component={RouterLink}
                to="/services/create"
                startIcon={<Iconify icon="material-symbols:add" />}
                disabled={user?.readonly}>
          {t('New service')}
        </Button>
      </Stack>
      <Grid container direction={'row'} alignItems={'flex-end'} justifyContent={'flex-end'} mb={2} spacing={2}>
        <Grid item xs={4}>
          <FormControl variant="standard" fullWidth>
            <InputLabel id="partner-select-label">{t('Partner')}</InputLabel>
            <Select
              labelId="partner-select-label"
              id="partner-select"
              value={selectedPartner ?? ''}
              label={t('Partner')}
              onChange={(e) => setSelectedPartner(e.target.value)}
            >
              <MenuItem key={'partner-not-set'} value={''}>{t('Not set')}</MenuItem>
              { partners.map(partner => (<MenuItem key={`partner-${partner.id}`} value={partner.id}>{partner.name}</MenuItem>)) }
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4} >
          <FormControl variant="standard" fullWidth>
            <InputLabel id="category-select-label">{t('Category')}</InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              value={selectedCategory ?? ''}
              label={t('Category')}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem key={'category-not-set'} value={''}>{t('Not set')}</MenuItem>
              { categories?.map(category => (<MenuItem key={`category-${category.id}`} value={category.id}>{category.name}</MenuItem>)) }
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <BaseTable
        columns={columns}
        query={query}
        forceReloadCounter={reloadCounter}
        loadCallback={async (query, page, pageSize) => {
          try {
            return await api.service.list(selectedPartner, selectedCategory, query, page, pageSize)
          } catch (e) {
            console.log('Failed to load services', e)
            return {
              data: [],
              totalCount: 0,
            }
          }
        }}
      />
    </Container>
  </Page>)

}